import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/context/auth-context';
import {
  requestFCMToken,
  listenForMessages,
  FCM_WEB_REGISTRATION_SESSION_KEY,
} from '@/lib/firebase';
import { usePostNotificationsFcmRegisterMutation } from '@/hooks/mutations/use-post-notifications-fcm-register.mutation';
import { useEnvironment } from '@/hooks/use-environment';
import { isWebview } from '@/utils/webview-detection';

export function FCMBootstrapper() {
  const { isAuthenticated, user } = useAuth();
  const hasInitializedMessagesRef = useRef(false);
  const registerMutation = usePostNotificationsFcmRegisterMutation();
  const { isStaging } = useEnvironment();
  const [currentPath, setCurrentPath] = useState(() =>
    typeof window !== 'undefined' ? window.location.pathname : ''
  );

  useEffect(() => {
    const updatePath = () => setCurrentPath(window.location.pathname);

    window.addEventListener('popstate', updatePath);

    const originalPushState = history.pushState;
    history.pushState = function (...args) {
      originalPushState.apply(history, args);
      updatePath();
    };

    return () => {
      window.removeEventListener('popstate', updatePath);
      history.pushState = originalPushState;
    };
  }, []);

  const isEmbedDisclosureConsentPage =
    currentPath === '/checkr/disclosure-consent-embed';

  useEffect(() => {
    if (hasInitializedMessagesRef.current) {
      return;
    }
    if (isWebview()) {
      return;
    }
    listenForMessages();
    hasInitializedMessagesRef.current = true;
  }, []);

  useEffect(() => {
    if (isWebview()) {
      return;
    }
    if (isEmbedDisclosureConsentPage) {
      return;
    }
    if (!isAuthenticated || !user?.userid) {
      return;
    }
    if (
      typeof Notification !== 'undefined' &&
      Notification.permission === 'denied'
    ) {
      return;
    }

    let cancelled = false;

    (async () => {
      const token = await requestFCMToken();
      if (!token || cancelled) {
        return;
      }

      const fingerprint = `${user.userid}:${token}`;
      try {
        if (
          sessionStorage.getItem(FCM_WEB_REGISTRATION_SESSION_KEY) ===
          fingerprint
        ) {
          return;
        }
      } catch {
        /* ignore sessionStorage */
      }

      try {
        await registerMutation.mutateAsync({
          token,
          device_type: 'web',
          userid: user.userid || '',
          environment: isStaging ? 'staging' : 'production',
        });
        try {
          sessionStorage.setItem(FCM_WEB_REGISTRATION_SESSION_KEY, fingerprint);
        } catch {
          /* ignore */
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to register FCM token', err);
      }
    })();

    return () => {
      cancelled = true;
    };
    // registerMutation.mutateAsync is stable; avoid effect churn from the mutation object reference
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isStaging only affects payload.environment
  }, [isAuthenticated, user?.userid, isStaging, isEmbedDisclosureConsentPage]);

  return null;
}
